import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { compose } from 'redux';

import {
  List,
  ListItem,
  ListItemColumn,
  ListBody,
  MaterialIcon,
  MenuList,
  MenuListItem,
  SVGIcon,
  HOCs,
  Checkbox,
  Button,
} from '@pearsontechnology/aero-components';



import BaseMoreButton from 'components/MoreButton';
const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);


import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { Transition } from 'react-transition-group';
import PlaceholderRow from 'components/PlaceholderList/PlaceholderRow';
import PublishStatus from '../PublishStatus';
import messages from './messages';
import PublicationDescription from './PublicationDescription';
import { getTime, isPostPublicationStatusFeatureDate } from '../../utils';
import { timeFormats, actionType, status, SVG_ICON_DOWNLOAD, DNU_FATAL_ERRORS, FATAL_ERROR_REPORT } from '../../utils/constants';
import {
  HistoryListItem,
  HistoryListWrapper,
  ColumnText,
  SubListHeader,
  DeleteButton,
  Menu,
  MenuListItemText,
  SubListHeaderActions,
  SubListHeaderActionsLeft,
  SubListHeaderActionsRight,
} from './styles';
import PublicationVersionCompare from './PublicationVersionCompare';
import { downloadFile } from 'utils/downloadFile';

const PublicationHistory = ({ show, historyList, actionHandler, theme, showCompareVersionUI, hanldeVersionUiActions, disabled }) => {

  const [selectedVersions, setSelectedVersion] = useState({});
  const [disableSelection, setDisableSelection] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  //TODO: Refactor after backend work
  //This is a temporary fix for export failed and old publications(pre publication status implementation)
  // once this is removed ,make sure to default the status and statusMessage to 'COMPLETED' and '10001' to
  // show all old publications as completed.
  const validatePublicationStatus = (publication) => {
    let status = publication.publicationJobStatus;
    let statusMessage = publication.statusMessage;
    let failureStatusCodes = publication.failureStatusCodes || [];
    //Any new publication after Nov 15th, 2021 with no publicationJobStatus && statusMessage will be interpreted as In progress.
    //Publications before Nov 15th, 2021 with no publicationJobStatus && statusMessage will be interpreted as complete to backfill old publication status.
    if (!status && !statusMessage) {
      //TODO: class on demand needs to check the output type here.
      if (isPostPublicationStatusFeatureDate(publication.updatedAt)) {
        status = 'INPROGRESS';
        statusMessage = '10002'; //export IN progress
      } else {
        status = 'COMPLETED';
        statusMessage = '10001';
      }
    }
    return {
      status,
      statusMessage,
      failureStatusCodes,
    };
  };
  
  const handleCPValidationReportDownload = async (publication) => {
    const { reports, title } = publication;
    const reportUrl = reports[0]['reportUrl'];
    const filename = `${title}${FATAL_ERROR_REPORT}`;
    
    await downloadFile(reportUrl, filename);
  };

  const handleS3EPUBDownload = async (publication) => {
    const { reports, title } = publication;
    const reportUrl = reports[0]['reportUrl'];
    const epubUrl = reportUrl.slice(0, reportUrl.lastIndexOf('/')) + '/output.epub';
    const filename = `${DNU_FATAL_ERRORS}${title}.epub`;

    await downloadFile(epubUrl, filename);
  };
  const handleCheck = (e) => {
    const publication = historyList[e.target.value];
    const { jobId } = publication;
    const updatedSelections = { ...selectedVersions };

    updatedSelections[jobId] ? delete updatedSelections[jobId] :
      updatedSelections[jobId] = {
        ...publication,
        version: historyList.length - e.target.value,
        time: getTime(publication.updatedAt, timeFormats.DATE_MONTH),
      };

    setDisableSelection(Object.keys(updatedSelections).length > 1);
    setSelectedVersion(updatedSelections);
  };

  const handleCancel = () => {
    setSelectedVersion({});
    setDisableSelection(false);
    setShowComparison(false);
    hanldeVersionUiActions(false);
  };

  const handleContinue = () => {
    setShowComparison(true);
  };

  const getSelectedVersions = () => Object.values(selectedVersions).sort((a, b) => a['version'] - b['version']);

  return (
    <Transition
      mountOnEnter
      unmountOnExit
      timeout={{ enter: 0, exit: 600 }}
      in={show}
    >
      {(state) => (
        <Fragment>
          <SubListHeader>{intl.formatMessage(showComparison ? messages.versionChanges : messages.publicationVersion)}</SubListHeader>
          {showCompareVersionUI && !showComparison && <SubListHeaderActions>
            <SubListHeaderActionsLeft>{intl.formatMessage(messages.selectVersions)}</SubListHeaderActionsLeft>
            <SubListHeaderActionsRight>
              <Button onClick={handleCancel}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button primary disabled={!disableSelection} onClick={handleContinue} >
                {intl.formatMessage(messages.continue)}
              </Button>
            </SubListHeaderActionsRight>
          </SubListHeaderActions>}
          <HistoryListWrapper showDifferences={showComparison} className={state} isScrollable={historyList?.length > 3} disabled={disabled}>
            {!showComparison && <Fragment>
              {historyList ?
                historyList.map((historyListItem, index) => (
                  <HistoryListItem key={historyListItem.publicationId}>
                    <List>
                      <ListBody>
                        <ListItem>
                          <ListItemColumn colWidth="50px" colWidthMobile="50%">
                            <ColumnText>{historyList.length - index}</ColumnText>
                          </ListItemColumn>
                          {showCompareVersionUI && <ListItemColumn colWidth="50px" colWidthMobile="50%">
                            <Checkbox onChange={handleCheck} value={index} checked={selectedVersions[historyListItem.jobId] ? true : false} disabled={disableSelection && !selectedVersions[historyListItem.jobId]} />
                          </ListItemColumn>}
                          <ListItemColumn colWidth="20%" colWidthMobile="50%">
                            <ColumnText>
                              {`${historyListItem.publisherGivenName} ${historyListItem.publisherFamilyName}`}
                              <br />
                              {getTime(historyListItem.updatedAt, timeFormats.MONTH_DATE)}
                            </ColumnText>
                          </ListItemColumn>
                          <ListItemColumn colWidth="10%" colWidthMobile="50%">
                            <ColumnText>
                              {`${historyListItem.packageSize || ''}`}
                            </ColumnText>
                          </ListItemColumn>
                          <ListItemColumn colWidth="40%" colWidthMobile="50%">
                            <PublicationDescription description={historyListItem.description} />
                          </ListItemColumn>
                          <ListItemColumn colWidth="10%" colWidthMobile="50%">
                            <ColumnText>
                              <PublishStatus itemIndex={index} publication={historyListItem} publishStatusInfo={validatePublicationStatus(historyListItem)} />
                            </ColumnText>
                          </ListItemColumn>
                          <ListItemColumn colWidth="30px" colWidthMobile="50%">
                            {historyList.length > 1 ?
                              <DeleteButton onClick={historyListItem.isDeleteDisabled ? null : actionHandler(historyListItem, actionType.DELETE)}>
                                <MaterialIcon
                                  id='cancel'
                                  style={{
                                    color: historyListItem.isDeleteDisabled ? theme['color.greyDarker'] : theme['color.redDark'],
                                    fontSize: '23px',
                                  }}
                                  center
                                />
                              </DeleteButton> : null
                            }
                          </ListItemColumn>
                          <ListItemColumn colWidth="30px" colWidthMobile="20px">
                            {(historyListItem.reports || (historyListItem.publicationJobStatus === status.FAILED)) && <MoreButtonWithPopper placement="bottom-end" hidepopperonclick>
                              <Menu>
                                <MenuList>
                                  <MenuListItem key={historyListItem.publicationId} onClick={() => { handleCPValidationReportDownload(historyListItem); }}>
                                    <SVGIcon icon="cloudDownload" />
                                    <MenuListItemText>
                                      {intl.formatMessage(messages.downloadReport)}
                                    </MenuListItemText>
                                  </MenuListItem>
                                  {historyListItem.publicationJobStatus === status.FAILED && <MenuListItem key={historyListItem.publicationId} onClick={() => { handleS3EPUBDownload(historyListItem); }}>
                                    <SVGIcon icon={SVG_ICON_DOWNLOAD} />
                                    <MenuListItemText>
                                      {intl.formatMessage(messages.downloadS3Epub)}
                                    </MenuListItemText>
                                  </MenuListItem>}
                                </MenuList>
                              </Menu>
                            </MoreButtonWithPopper>}
                          </ListItemColumn>
                        </ListItem>
                      </ListBody>
                    </List>
                  </HistoryListItem>)
                ) : <ListBody>
                  <PlaceholderRow />
                  <PlaceholderRow />
                </ListBody>
              }
            </Fragment>}
            {showComparison && <PublicationVersionCompare onClose={setShowComparison} selectedPublications={getSelectedVersions()} />}
          </HistoryListWrapper>
        </Fragment>)}
    </Transition>
  );
};

PublicationHistory.propTypes = {
  historyList: PropTypes.array,
  show: PropTypes.bool,
  showCompareVersionUI: PropTypes.bool,
  hanldeVersionUiActions: PropTypes.func,
};

export default compose(withTheme)(PublicationHistory);
